import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Native } from '@givebacks/api';
import { Box, MantineColor, parseThemeColor, px, useMantineTheme } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';

import { cx } from '../Provider/Provider';
import { SwipeableProvider, useSwipeableContext } from './Swipeable.context';
import classes from './Swipeable.module.css';

type SwipeableActionProps = {
  /**
   * Whether the action is currently ready i.e. the user has swiped passed the threshold
   */
  isReady: boolean;
  /**
   * Whether the action is on the left side of the swipeable component.
   * NOTE: This is already handled by the parent `Swipeable` component.
   */
  isLeft: boolean;
  /**
   * Callback to be fired when the user has swiped to trigger an action
   */
  onSwipe?: () => void;
  /**
   * The color of the action
   */
  color: MantineColor;
  /**
   * The content of the action.
   * Can be a ReactNode or a callback that receives `isReady` as an argument,
   * allowing for conditional styling when the user has swiped passed the threshold
   */
  children: React.ReactNode;

  /**
   * Head Start Width: Width to add to the action to account for radius
   * Automatically adjusted for left or right using the `isLeft` prop
   *
   * Defaults to Theme.Radius.Md
   *
   */
  headStartWidth?: number;
};

const SwipeableAction = ({
  isReady,
  onSwipe,
  isLeft,
  color,
  children,
  headStartWidth = 0,
}: SwipeableActionProps) => {
  const theme = useMantineTheme();
  const { x, leftActionRef, rightActionRef } = useSwipeableContext();
  const { value } = parseThemeColor({
    color,
    theme,
  });

  const memoizedOnSwipe = useCallback(() => onSwipe?.(), [onSwipe]);

  const memoizeHeadStart = useMemo(
    () => (headStartWidth ? headStartWidth : Number(px(theme.radius.md))),
    [headStartWidth, theme.radius.md]
  );

  useEffect(() => {
    if (isLeft) {
      leftActionRef.current = memoizedOnSwipe;
    } else {
      rightActionRef.current = memoizedOnSwipe;
    }
  }, []);

  return (
    <animated.div
      className={cx(classes.SwipeableAction, {
        [classes['SwipeableAction--ready']]: isReady,
        [classes['SwipeableAction--left']]: isLeft,
        [classes['SwipeableAction--right']]: !isLeft,
      })}
      style={{
        width: x.to((val) => `${isLeft ? val - memoizeHeadStart : -val + memoizeHeadStart}px`),
        background: value,
      }}
    >
      <div
        className={cx(classes.SwipeableAction__children, {
          [classes['SwipeableAction__children--left']]: isLeft,
          [classes['SwipeableAction__children--right']]: !isLeft,
        })}
      >
        {children}
      </div>
    </animated.div>
  );
};

type SwipeableActionRenderProps = Pick<SwipeableActionProps, 'isLeft' | 'isReady'>;

type SwipeableProps = {
  /**
   * The threshold in pixels that the user has to swipe past to trigger the action
   * @default 200
   */
  threshold?: number;
  /**
   * The action to be displayed on the right side of the swipeable component
   */
  rightAction?: (props: SwipeableActionRenderProps) => React.ReactNode;
  /**
   * The action to be displayed on the left side of the swipeable component
   */
  leftAction?: (props: SwipeableActionRenderProps) => React.ReactNode;
  /**
   * Optional callback to be fired when the user has swiped passed the threshold
   */
  onPassThreshold?: (dir?: 'left' | 'right') => void;
  /**
   * Children to add swipe functionality to
   */
  children: React.ReactNode;
};

const Swipeable = ({
  rightAction,
  leftAction,
  onPassThreshold,
  children,
  threshold = 100,
}: SwipeableProps) => {
  const [{ x }, api] = useSpring(() => ({ x: 0 }));
  const leftActionHandlerRef = useRef<(() => void) | undefined>();
  const rightActionHandlerRef = useRef<(() => void) | undefined>();
  const passedThresholdRef = useRef(false);
  const [isLeftReady, setIsLeftReady] = useState(false);
  const [isRightReady, setIsRightReady] = useState(false);

  const fireLeftAction = useDebouncedCallback(() => {
    leftActionHandlerRef?.current?.();
  }, 1);

  const fireRightAction = useDebouncedCallback(() => {
    rightActionHandlerRef?.current?.();
  }, 1);

  const handleHaptic = useCallback(({ dir }: { dir: 'left' | 'right' }) => {
    const leftAction = leftActionHandlerRef?.current;
    const rightAction = rightActionHandlerRef?.current;

    // Only call haptic feedback when the user has swiped passed the threshold with the same action direction
    if (dir === 'right' && rightAction) {
      Native.haptic('light');
    } else if (dir == 'left' && leftAction) {
      Native.haptic('light');
    }
  }, []);

  // Set the drag hook and define component movement based on gesture data
  const bind = useDrag(
    ({
      args: [{ doesLeftActionExist, doesRightActionExist }],
      down,
      movement: [mx],
      direction: [xDir],
      active,
      currentTarget,
    }) => {
      const element = currentTarget as HTMLElement;
      const targetWidth = element.getBoundingClientRect().width;

      // if the item has been swiped right passed the threshold
      const isLeftActionReady = mx > threshold;
      // if the item has been swiped left passed the threshold
      const isRightActionReady = mx < -threshold;

      setIsLeftReady(isLeftActionReady);
      setIsRightReady(isRightActionReady);

      const hasSwipedPassedThreshold =
        (isLeftActionReady && xDir === 1) || (isRightActionReady && xDir === -1);

      // allow an optional callback to fired when user has swiped passed the threshold
      // e.g. firing a vibrate effect
      if (hasSwipedPassedThreshold) {
        // since this drag event continues to fire for any amount of dragging, this callback would have been called multiple times
        // so we use a ref to ensure it's only called once
        if (!passedThresholdRef.current) {
          passedThresholdRef.current = true;

          handleHaptic({ dir: xDir === 1 ? 'left' : 'right' });
          onPassThreshold?.(xDir === 1 ? 'left' : 'right');
        }
      } else {
        // we reset the ref to false so that the callback can be called again
        passedThresholdRef.current = false;
      }

      // fire the corresponding actions when the user has stopped dragging past the threshold
      if (!active) {
        if (isLeftActionReady) {
          fireLeftAction?.();
        }

        if (isRightActionReady) {
          fireRightAction?.();
        }
      }

      let xValue = 0;

      if (active) {
        const isDraggingInNonexistentDirection =
          (!doesLeftActionExist && mx > 0) || (!doesRightActionExist && mx < 0);

        if (isDraggingInNonexistentDirection) {
          // Don't drag when the user attempts to drag in a direction that doesn't have an action
          xValue = 0;
        } else if (hasSwipedPassedThreshold) {
          // Once the user has dragged passed the threshold, move the draggable all the way to the left or right
          xValue = targetWidth * xDir;
        } else if (down) {
          // Otherwise, move with the user's drag
          xValue = mx;
        }
      }

      const config = {
        friction: active ? 50 : 20,
        tension: active ? 800 : 200,
        bounce: 0,
      };

      api.start({
        x: xValue,
        config: config,
      });
    },
    {
      axis: 'x',
    }
  );

  return (
    <SwipeableProvider
      value={{
        x,
        leftActionRef: leftActionHandlerRef,
        rightActionRef: rightActionHandlerRef,
      }}
    >
      <Box className={classes.Swipeable__container}>
        {leftAction?.({ isLeft: true, isReady: isLeftReady }) ?? null}
        {rightAction?.({ isLeft: false, isReady: isRightReady }) ?? null}

        <animated.div
          {...bind({
            doesLeftActionExist: !!leftAction,
            doesRightActionExist: !!rightAction,
          })}
          className={classes.Swipeable}
          style={{
            x,
          }}
        >
          {children}
        </animated.div>
      </Box>
    </SwipeableProvider>
  );
};

Swipeable.Action = SwipeableAction;

export { Swipeable };
